import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users as UsersIcon, Cpu, Activity, Bell, RefreshCw, AlertCircle, CheckCircle, XCircle, Plus } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { apiGet } from '../api/client';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
interface Stats {
  totalUsers: number;
  newUsersThisWeek: number;
  activeDevices: number;
  sessionsToday: number;
  activeAlerts: number;
  syncsLast24h: number;
}
interface ActivityItem {
  id: string;
  type: 'session' | 'alert' | 'sync_success' | 'sync_failed';
  description: string;
  timestamp: string;
  relativeTime: string;
}
export function Dashboard() {
  const {
    token
  } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats>({
    totalUsers: 0,
    newUsersThisWeek: 0,
    activeDevices: 0,
    sessionsToday: 0,
    activeAlerts: 0,
    syncsLast24h: 0
  });
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [sessionData, setSessionData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  useEffect(() => {
    loadDashboardData();
  }, []);
  const loadDashboardData = async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      const statsRes = await apiGet('/admin/stats', token);
      setStats({
        totalUsers: statsRes.totalUsers || 0,
        newUsersThisWeek: statsRes.newUsersThisWeek || 0,
        activeDevices: statsRes.activeDevices || 0,
        sessionsToday: statsRes.sessionsToday || 0,
        activeAlerts: statsRes.activeAlerts || 0,
        syncsLast24h: statsRes.syncsLast24h || 0
      });
      // Fetch recent activity: interleave most recent sessions and alerts
      try {
        const [recentSessionsRes, recentAlertsRes] = await Promise.all([apiGet('/admin/sessions/all?limit=5&sortBy=startedAt&order=desc', token), apiGet('/admin/alerts/all?limit=5&sortBy=triggeredAt&order=desc', token)]);
        const recentSessions = (recentSessionsRes.sessions || recentSessionsRes.data || []).map((s: any) => ({
          id: `session-${s.id}`,
          type: 'session' as const,
          description: s.user?.fullName ? `Session started by ${s.user.fullName}` : 'Session started',
          timestamp: s.startedAt,
          relativeTime: getRelativeTimeStr(s.startedAt)
        }));
        const recentAlerts = (recentAlertsRes.alerts || recentAlertsRes.data || []).map((a: any) => ({
          id: `alert-${a.id}`,
          type: 'alert' as const,
          description: a.message || 'Alert triggered',
          timestamp: a.triggeredAt,
          relativeTime: getRelativeTimeStr(a.triggeredAt)
        }));
        const combined = [...recentSessions, ...recentAlerts].filter(i => i.timestamp).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 5);
        setRecentActivity(combined);
      } catch (e) {
        console.error('Failed to load recent activity:', e);
        setRecentActivity([]);
      }
      // Fetch real user growth data (last 30 days)
      const now = new Date();
      try {
        const usersRes = await apiGet('/admin/users/all?limit=100&sortBy=createdAt&order=asc', token);
        const usersArr = usersRes.users || [];
        const last30DaysMap: Record<string, number> = {};
        for (let i = 29; i >= 0; i--) {
          const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const key = d.toISOString().split('T')[0];
          last30DaysMap[key] = 0;
        }
        usersArr.forEach((u: any) => {
          if (u.createdAt) {
            const key = new Date(u.createdAt).toISOString().split('T')[0];
            if (key in last30DaysMap) last30DaysMap[key]++;
          }
        });
        const userGrowth = Object.entries(last30DaysMap).map(([dateStr, count]) => ({
          date: new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          users: count
        }));
        setUserGrowthData(userGrowth);
      } catch (e) {
        console.error('Failed to load user growth data:', e);
        setUserGrowthData([]);
      }
      // Fetch real sessions data (last 7 days)
      try {
        const sessionsRes = await apiGet('/admin/sessions/all?limit=100&sortBy=startedAt&order=asc', token);
        const sessionsArr = sessionsRes.sessions || sessionsRes.data || [];
        const last7DaysMap: Record<string, number> = {};
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const key = d.toISOString().split('T')[0];
          last7DaysMap[key] = 0;
        }
        sessionsArr.forEach((s: any) => {
          if (s.startedAt) {
            const key = new Date(s.startedAt).toISOString().split('T')[0];
            if (key in last7DaysMap) last7DaysMap[key]++;
          }
        });
        const sessionsByDay = Object.entries(last7DaysMap).map(([dateStr, count]) => ({
          date: new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          sessions: count
        }));
        setSessionData(sessionsByDay);
      } catch (e) {
        console.error('Failed to load session data:', e);
        setSessionData([]);
      }
    } catch (err: any) {
      console.error('Dashboard load error:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };
  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  const getRelativeTimeStr = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'session':
        return <Activity className="w-4 h-4 text-info" data-id="element-420" />;
      case 'alert':
        return <AlertCircle className="w-4 h-4 text-danger" data-id="element-421" />;
      case 'sync_success':
        return <CheckCircle className="w-4 h-4 text-success" data-id="element-422" />;
      case 'sync_failed':
        return <XCircle className="w-4 h-4 text-danger" data-id="element-423" />;
      default:
        return <Activity className="w-4 h-4 text-text-secondary" data-id="element-424" />;
    }
  };
  const getActivityBadge = (type: string) => {
    switch (type) {
      case 'session':
        return <span className="px-2 py-0.5 rounded-badge bg-[rgba(59,130,246,0.1)] text-info text-xs border border-[rgba(59,130,246,0.2)]" data-id="element-425">
            Session
          </span>;
      case 'alert':
        return <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-xs border border-[rgba(239,68,68,0.2)]" data-id="element-426">
            Alert
          </span>;
      case 'sync_success':
      case 'sync_failed':
        return <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-xs border border-[rgba(245,158,11,0.2)]" data-id="element-427">
            Sync
          </span>;
      default:
        return null;
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center h-64" data-id="element-428">
        <div className="text-text-secondary" data-id="element-429">Loading dashboard...</div>
      </div>;
  }
  return <div data-id="element-430">
      <div className="flex justify-between items-center mb-8" data-id="element-431">
        <div data-id="element-432">
          <h1 className="text-[24px] font-semibold text-white" data-id="element-433">Dashboard</h1>
          <p className="text-[14px] text-text-secondary mt-1" data-id="element-434">
            Overview of system activity
          </p>
        </div>
      </div>

      {error && <div className="mb-6 p-4 bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] rounded-card text-danger" data-id="element-435">
          {error}
        </div>}

      {/* Stats Row */}
      <div className="grid grid-cols-5 gap-4 mb-8" data-id="element-436">
        <div className="bg-surface border border-border rounded-card p-5" data-id="element-437">
          <div className="flex justify-between items-start mb-3" data-id="element-438">
            <span className="text-[12px] text-text-secondary uppercase tracking-wider" data-id="element-439">
              Total Users
            </span>
            <UsersIcon className="w-5 h-5 text-accent" data-id="element-440" />
          </div>
          <div className="text-[32px] font-bold text-white mb-2" data-id="element-441">
            {stats.totalUsers}
          </div>
          <div className={`text-[12px] ${stats.newUsersThisWeek > 0 ? 'text-success' : 'text-text-secondary'}`} data-id="element-442">
            {stats.newUsersThisWeek > 0 ? `+${stats.newUsersThisWeek} this week` : 'No change this week'}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-card p-5" data-id="element-443">
          <div className="flex justify-between items-start mb-3" data-id="element-444">
            <span className="text-[12px] text-text-secondary uppercase tracking-wider" data-id="element-445">
              Active Devices
            </span>
            <Cpu className="w-5 h-5 text-info" data-id="element-446" />
          </div>
          <div className="text-[32px] font-bold text-white mb-2" data-id="element-447">
            {stats.activeDevices}
          </div>
          <div className="text-[12px] text-text-secondary" data-id="element-448">
            Currently online
          </div>
        </div>

        <div className="bg-surface border border-border rounded-card p-5" data-id="element-449">
          <div className="flex justify-between items-start mb-3" data-id="element-450">
            <span className="text-[12px] text-text-secondary uppercase tracking-wider" data-id="element-451">
              Sessions Today
            </span>
            <Activity className="w-5 h-5 text-success" data-id="element-452" />
          </div>
          <div className="text-[32px] font-bold text-white mb-2" data-id="element-453">
            {stats.sessionsToday}
          </div>
          <div className="text-[12px] text-text-secondary" data-id="element-454">Today</div>
        </div>

        <div className={`bg-surface border rounded-card p-5 ${stats.activeAlerts > 0 ? 'border-[rgba(239,68,68,0.3)]' : 'border-border'}`} data-id="element-455">
          <div className="flex justify-between items-start mb-3" data-id="element-456">
            <span className="text-[12px] text-text-secondary uppercase tracking-wider" data-id="element-457">
              Active Alerts
            </span>
            <Bell className="w-5 h-5 text-danger" data-id="element-458" />
          </div>
          <div className="text-[32px] font-bold text-white mb-2" data-id="element-459">
            {stats.activeAlerts}
          </div>
          <div className={`text-[12px] ${stats.activeAlerts > 0 ? 'text-danger' : 'text-success'}`} data-id="element-460">
            {stats.activeAlerts > 0 ? 'Requires attention' : 'All clear'}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-card p-5" data-id="element-461">
          <div className="flex justify-between items-start mb-3" data-id="element-462">
            <span className="text-[12px] text-text-secondary uppercase tracking-wider" data-id="element-463">
              Syncs (24h)
            </span>
            <RefreshCw className="w-5 h-5 text-warning" data-id="element-464" />
          </div>
          <div className="text-[32px] font-bold text-white mb-2" data-id="element-465">
            {stats.syncsLast24h}
          </div>
          <div className="text-[12px] text-text-secondary" data-id="element-466">Last 24 hours</div>
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div className="bg-surface border border-border rounded-card mb-8" data-id="element-467">
        <div className="p-5 border-b border-border" data-id="element-468">
          <h2 className="text-[16px] font-semibold text-white" data-id="element-469">
            Recent Activity
          </h2>
        </div>
        <div className="divide-y divide-[#1E1E1E]" data-id="element-470">
          {recentActivity.length === 0 ? <div className="p-5 text-center text-text-secondary" data-id="element-471">
              No recent activity
            </div> : recentActivity.map(item => <div key={item.id} className="p-4 flex items-center gap-4 hover:bg-[#1E1E1E] transition-colors" data-id="element-472">
                <div className="w-7 h-7 rounded-full bg-surface-elevated flex items-center justify-center flex-shrink-0" data-id="element-473">
                  {getActivityIcon(item.type)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-474">
                  <p className="text-[14px] text-white truncate" data-id="element-475">
                    {item.description}
                  </p>
                  <p className="text-[12px] text-text-secondary" data-id="element-476">
                    {item.relativeTime}
                  </p>
                </div>
                <div className="flex-shrink-0" data-id="element-477">
                  {getActivityBadge(item.type)}
                </div>
              </div>)}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-478">
        <div className="bg-surface border border-border rounded-card p-5" data-id="element-479">
          <h3 className="text-[14px] font-semibold text-white mb-4" data-id="element-480">
            User Growth
          </h3>
          {userGrowthData.length === 0 ? <div className="flex items-center justify-center h-[200px]" data-id="element-481">
              <span className="text-[14px] text-text-tertiary" data-id="element-482">
                No user data yet
              </span>
            </div> : <ResponsiveContainer width="100%" height={200} data-id="element-483">
              <LineChart data={userGrowthData} data-id="element-484">
                <XAxis dataKey="date" stroke="#555555" style={{
              fontSize: '12px'
            }} tick={{
              fill: '#555555'
            }} data-id="element-485" />
                <YAxis stroke="#555555" style={{
              fontSize: '12px'
            }} tick={{
              fill: '#555555'
            }} data-id="element-486" />
                <Tooltip contentStyle={{
              backgroundColor: '#1A1A1A',
              border: '1px solid #2A2A2A',
              borderRadius: '8px',
              color: '#FFFFFF'
            }} data-id="element-487" />
                <Line type="monotone" dataKey="users" stroke="#6B21E8" strokeWidth={2} dot={false} data-id="element-488" />
              </LineChart>
            </ResponsiveContainer>}
        </div>

        <div className="bg-surface border border-border rounded-card p-5" data-id="element-489">
          <h3 className="text-[14px] font-semibold text-white mb-4" data-id="element-490">
            Daily Sessions
          </h3>
          {sessionData.length === 0 ? <div className="flex items-center justify-center h-[200px]" data-id="element-491">
              <span className="text-[14px] text-text-tertiary" data-id="element-492">
                No session data yet
              </span>
            </div> : <ResponsiveContainer width="100%" height={200} data-id="element-493">
              <BarChart data={sessionData} data-id="element-494">
                <XAxis dataKey="date" stroke="#555555" style={{
              fontSize: '12px'
            }} tick={{
              fill: '#555555'
            }} data-id="element-495" />
                <YAxis stroke="#555555" style={{
              fontSize: '12px'
            }} tick={{
              fill: '#555555'
            }} data-id="element-496" />
                <Tooltip contentStyle={{
              backgroundColor: '#1A1A1A',
              border: '1px solid #2A2A2A',
              borderRadius: '8px',
              color: '#FFFFFF'
            }} data-id="element-497" />
                <Bar dataKey="sessions" fill="#6B21E8" opacity={0.8} radius={[4, 4, 0, 0]} data-id="element-498" />
              </BarChart>
            </ResponsiveContainer>}
        </div>
      </div>

      {/* Quick Actions Row */}
      <div className="flex gap-4" data-id="element-499">
        <button onClick={() => navigate('/users')} className="px-4 py-2 bg-accent text-background font-semibold rounded-btn hover:opacity-90 transition-opacity flex items-center gap-2" data-id="element-500">
          <Plus className="w-4 h-4" data-id="element-501" />
          Add User
        </button>
        <button onClick={() => navigate('/devices')} className="px-4 py-2 bg-accent text-background font-semibold rounded-btn hover:opacity-90 transition-opacity flex items-center gap-2" data-id="element-502">
          <Plus className="w-4 h-4" data-id="element-503" />
          Add Device
        </button>
        <button onClick={() => navigate('/alerts?status=active')} className="px-4 py-2 bg-transparent text-danger border border-danger rounded-btn hover:bg-[rgba(239,68,68,0.1)] transition-colors" data-id="element-504">
          View Active Alerts
        </button>
        <button onClick={() => alert('Coming soon')} className="px-4 py-2 bg-transparent text-white border border-border rounded-btn hover:bg-surface transition-colors" data-id="element-505">
          Export Data
        </button>
      </div>
    </div>;
}